import { useState, useRef, useEffect } from 'react';
import '../styles/editable-field.css';

/**
 * EditableField — click-to-edit inline component.
 *
 * Props:
 *  value          — current value (string | number)
 *  onSave         — callback(newValue) called on blur / Enter
 *  type           — 'text' | 'number' | 'textarea' (default 'text')
 *  placeholder    — text shown when empty
 *  displayFormat  — optional fn(value) → string for formatted display
 *  className      — extra class on the wrapper
 *  style          — extra inline styles on the display span
 *  rows           — textarea rows (default 6)
 */
export default function EditableField({
  value,
  onSave,
  type = 'text',
  placeholder = 'Click to edit',
  displayFormat = null,
  className = '',
  style = {},
  rows = 6,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]   = useState(value != null ? String(value) : '');
  const inputRef            = useRef(null);

  // keep draft in sync if parent value changes while not editing
  useEffect(() => {
    if (!editing) setDraft(value != null ? String(value) : '');
  }, [value, editing]);

  // focus + select on open
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const saved = type === 'number' ? (Number(draft) || 0) : draft;
    onSave?.(saved);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value != null ? String(value) : '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { cancel(); return; }
    if (e.key === 'Enter' && type !== 'textarea') commit();
  };

  const sharedInputProps = {
    ref: inputRef,
    value: draft,
    onChange: (e) => setDraft(e.target.value),
    onBlur: commit,
    onKeyDown: handleKeyDown,
    className: `ef-input ${className}`,
  };

  if (editing) {
    return type === 'textarea'
      ? <textarea {...sharedInputProps} rows={rows} />
      : <input {...sharedInputProps} type={type === 'number' ? 'number' : 'text'} />;
  }

  const displayValue = displayFormat ? displayFormat(value) : (value ?? '');

  return (
    <span
      className={`ef-display ${className}`}
      style={style}
      onClick={() => setEditing(true)}
      title="Click to edit"
    >
      {displayValue !== '' && displayValue != null
        ? displayValue
        : <em className="ef-placeholder">{placeholder}</em>}
      <span className="ef-pencil" aria-hidden="true">✎</span>
    </span>
  );
}
